import { useState, useEffect, useRef, useCallback } from 'react';
import { Preferences } from '@capacitor/preferences';
import { App as CapacitorApp } from '@capacitor/app';
import { TimerConfig, TimerState, TimerSegment } from '../types/round-timer';
import { getTotalTimerTime, getTimerSequenceBasedOnConfig } from '../utils/timer-helpers';
import logger from '../utils/logger';
import { soundService, SoundType } from '../services/sound.service';
import {
  scheduleTimerNotifications,
  cancelTimerNotifications,
  requestTimerNotificationPermissions
} from '../services/timer-notifications.service';

// Storage key for timer state
const TIMER_STATE_KEY = 'roundTimer:state';
const TIMER_CONFIG_KEY = 'roundTimer:config';

/**
 * Save timer state to persistent storage
 */
const saveTimerState = async (state: TimerState, config: TimerConfig) => {
  try {
    await Preferences.set({
      key: TIMER_STATE_KEY,
      value: JSON.stringify(state)
    });
    await Preferences.set({
      key: TIMER_CONFIG_KEY,
      value: JSON.stringify(config)
    });
    logger.info('[useRoundTimer] Timer state saved');
  } catch (error) {
    logger.error('[useRoundTimer] Failed to save timer state:', error);
  }
};

/**
 * Load timer state from persistent storage
 */
const loadTimerState = async (): Promise<{ state: TimerState | null; config: TimerConfig | null }> => {
  try {
    const [stateResult, configResult] = await Promise.all([
      Preferences.get({ key: TIMER_STATE_KEY }),
      Preferences.get({ key: TIMER_CONFIG_KEY })
    ]);

    const state = stateResult.value ? JSON.parse(stateResult.value) : null;
    const config = configResult.value ? JSON.parse(configResult.value) : null;

    logger.info('[useRoundTimer] Timer state loaded:', { hasState: !!state, hasConfig: !!config });
    return { state, config };
  } catch (error) {
    logger.error('[useRoundTimer] Failed to load timer state:', error);
    return { state: null, config: null };
  }
};

/**
 * Clear timer state from persistent storage
 */
const clearTimerState = async () => {
  try {
    await Promise.all([
      Preferences.remove({ key: TIMER_STATE_KEY }),
      Preferences.remove({ key: TIMER_CONFIG_KEY })
    ]);
    logger.info('[useRoundTimer] Timer state cleared');
  } catch (error) {
    logger.error('[useRoundTimer] Failed to clear timer state:', error);
  }
};

// Default timer configuration
export const defaultTimerConfig: TimerConfig = {
  title: 'Marune Timer',
  rounds: 6,
  roundsLabel: 'Rounds',
  sound: true,
  repeat: false,
  round: {
    time: 120, // 2 minutes
    rest: 60, // 1 minute
    label: 'GO TIME',
    restLabel: 'REST',
    color: '#3b82f6', // blue-500
    restColor: '#ef4444', // red-500
    textColor: '#ffffff', // white
  },
  warmUp: {
    active: true,
    time: 120, // 2 minutes
    label: 'WARM UP',
    color: '#eab308', // yellow-500
    textColor: '#1f2937', // gray-800
  },
  coolDown: {
    active: true,
    time: 120, // 2 minutes
    label: 'COOL DOWN',
    color: '#eab308', // yellow-500
    textColor: '#1f2937', // gray-800
  }
};

// Initial timer state
const initialTimerState: TimerState = {
  isRunning: false,
  isCompleted: false,
  timerStarted: false,
  currentTimerIndex: 0,
  currentTrainRoundIndex: 0,
  elapsedTime: 0,
  totalTime: 0,
  timerSequence: [],
  segmentTimeLeft: 0,
};

/**
 * Custom hook to manage a round timer
 */
export const useRoundTimer = (initialConfig: TimerConfig = defaultTimerConfig) => {
  // Timer configuration
  const [timerConfig, setTimerConfig] = useState<TimerConfig>(initialConfig);
  
  // Timer state
  const [timerState, setTimerState] = useState<TimerState>(initialTimerState);
  
  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Timer interval reference
  const timerInterval = useRef<number | null>(null);

  // Sound play function using sound service
  const playSound = useCallback(async (soundType: SoundType) => {
    if (!timerConfig.sound) return;

    try {
      await soundService.play(soundType);
      logger.info(`Playing sound: ${soundType}`);
    } catch (error) {
      logger.error(`Failed to play sound ${soundType}:`, error);
    }
  }, [timerConfig.sound]);
  
  // Speech function (to be implemented with actual speech API)
  const speak = useCallback((text: string) => {
    if (timerConfig.sound) {
      // This would be implemented with a speech library
      logger.info(`Speaking: ${text}`);
    }
  }, [timerConfig.sound]);

  // Initialize sound service
  useEffect(() => {
    soundService.initialize()
      .then(() => logger.info('[useRoundTimer] Sound service initialized'))
      .catch(error => logger.error('[useRoundTimer] Sound service initialization failed:', error));

    return () => {
      soundService.cleanup()
        .then(() => logger.info('[useRoundTimer] Sound service cleaned up'))
        .catch(error => logger.error('[useRoundTimer] Sound service cleanup failed:', error));
    };
  }, []);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    let listenerHandle: any = null;

    const setupListener = async () => {
      listenerHandle = await CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          // App came to foreground
          logger.info('[useRoundTimer] App foregrounded');

          // Try to restore timer state
          const { state: savedState, config: savedConfig } = await loadTimerState();

          if (savedState && savedConfig && savedState.timerStarted && !savedState.isCompleted) {
            logger.info('[useRoundTimer] Restoring timer state from storage');

            // Check if target time has passed
            if (savedState.targetTime) {
              const now = Date.now();

              if (now < savedState.targetTime) {
                // Timer still running - restore state
                const newSegmentTimeLeft = Math.ceil((savedState.targetTime - now) / 1000);

                setTimerConfig(savedConfig);
                setTimerState({
                  ...savedState,
                  segmentTimeLeft: newSegmentTimeLeft
                });

                logger.info('[useRoundTimer] Timer state restored, resuming');
              } else {
                // Timer segment completed while backgrounded
                logger.info('[useRoundTimer] Timer segment completed in background');

                // We need to advance to next segment or complete
                // This will be handled by the interval when it starts
                setTimerConfig(savedConfig);
                setTimerState(savedState);
              }
            }
          }
        } else {
          // App went to background
          logger.info('[useRoundTimer] App backgrounded');

          // State is automatically saved by the save effect when running
        }
      });
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, []);

  // Initialize timer when config changes
  useEffect(() => {
    const totalTime = getTotalTimerTime(timerConfig);
    const timerSequence = getTimerSequenceBasedOnConfig(timerConfig);

    setTimerState(prevState => ({
      ...prevState,
      totalTime,
      timerSequence,
      segmentTimeLeft: timerSequence.length > 0 ? timerSequence[0].time : 0,
    }));
  }, [timerConfig]);

  // Save timer state when running (for background persistence)
  useEffect(() => {
    if (timerState.isRunning && timerState.timerStarted) {
      saveTimerState(timerState, timerConfig);
    } else if (timerState.isCompleted) {
      // Clear saved state when timer completes
      clearTimerState();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: the listed deps are the intended triggers
  }, [timerState.isRunning, timerState.timerStarted, timerState.isCompleted, timerState.currentTimerIndex, timerConfig]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        window.clearInterval(timerInterval.current);
      }
      // Cancel any scheduled notifications on unmount
      cancelTimerNotifications().catch(err =>
        logger.error('[useRoundTimer] Error cancelling notifications on unmount:', err)
      );
    };
  }, []);

  // Start the timer
  const startTimer = useCallback(async () => {
    const { timerSequence } = timerState;

    if (timerSequence.length === 0) return;

    let trainedRounds = 0;
    if (timerSequence[0].type === 'round') {
      trainedRounds = 1;
    }

    speak(timerSequence[0].label);
    playSound('bell');

    // Request notification permissions and schedule notifications for background alerts
    const hasPermission = await requestTimerNotificationPermissions();
    if (hasPermission) {
      await scheduleTimerNotifications(timerSequence, 0);
      logger.info('[useRoundTimer] Scheduled background notifications');
    } else {
      logger.warn('[useRoundTimer] Notification permission not granted - timer will only work in foreground');
    }

    // Calculate target completion time for first segment
    const now = Date.now();
    const targetTime = now + (timerSequence[0].time * 1000);

    setTimerState(prevState => ({
      ...prevState,
      currentTimerIndex: 0,
      currentTrainRoundIndex: trainedRounds,
      isRunning: true,
      timerStarted: true,
      isCompleted: false,
      segmentTimeLeft: timerSequence[0].time,
      targetTime,
    }));

    // Start the interval - check every 100ms for accuracy
    if (timerInterval.current === null) {
      timerInterval.current = window.setInterval(() => {
        setTimerState(prevState => {
          if (!prevState.isRunning || !prevState.targetTime) return prevState;

          const now = Date.now();
          const newSegmentTimeLeft = Math.max(0, Math.ceil((prevState.targetTime - now) / 1000));

          // Calculate elapsed time based on current segment
          const segmentDuration = prevState.timerSequence[prevState.currentTimerIndex]?.time || 0;
          const segmentElapsed = segmentDuration - newSegmentTimeLeft;
          const newElapsedTime = prevState.elapsedTime + (segmentElapsed > 0 ? 1 : 0);
          const newTotalTime = prevState.totalTime - (segmentElapsed > 0 ? 1 : 0);

          // Check if current segment is finished
          if (newSegmentTimeLeft <= 0) {
            const nextTimerIndex = prevState.currentTimerIndex + 1;
            const totalTimers = prevState.timerSequence.length;

            // Check if timer is completed
            if (nextTimerIndex >= totalTimers) {
              playSound('complete');

              if (timerInterval.current) {
                window.clearInterval(timerInterval.current);
                timerInterval.current = null;
              }

              return {
                ...prevState,
                isCompleted: true,
                isRunning: false,
                elapsedTime: newElapsedTime,
                totalTime: 0,
                segmentTimeLeft: 0,
                targetTime: undefined,
              };
            }

            // Calculate trained rounds
            const trainedRounds =
              prevState.timerSequence[nextTimerIndex] &&
              prevState.timerSequence[nextTimerIndex].type === 'round'
                ? prevState.currentTrainRoundIndex + 1
                : prevState.currentTrainRoundIndex;

            // Speak the name of the next segment
            if (prevState.timerSequence[nextTimerIndex]) {
              speak(prevState.timerSequence[nextTimerIndex].label);
              playSound('bell');
            }

            // Set new target time for next segment
            const nextSegmentDuration = prevState.timerSequence[nextTimerIndex].time;
            const newTargetTime = Date.now() + (nextSegmentDuration * 1000);

            return {
              ...prevState,
              currentTimerIndex: nextTimerIndex,
              currentTrainRoundIndex: trainedRounds,
              segmentTimeLeft: nextSegmentDuration,
              elapsedTime: newElapsedTime,
              totalTime: newTotalTime,
              targetTime: newTargetTime,
            };
          }

          return {
            ...prevState,
            segmentTimeLeft: newSegmentTimeLeft,
          };
        });
      }, 100); // Check every 100ms for smoother updates
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: the listed deps are the intended triggers
  }, [timerState.timerSequence, speak, playSound]);

  // Toggle timer (play/pause)
  const toggleTimer = useCallback(async () => {
    const { timerStarted, isRunning, timerSequence, currentTimerIndex, segmentTimeLeft } = timerState;

    // If timer hasn't started yet, start it
    if (!timerStarted) {
      startTimer();
      return;
    }

    // Timer already started - toggle play/pause
    if (isRunning) {
      playSound('beep');

      // Cancel scheduled notifications when pausing
      await cancelTimerNotifications();
      logger.info('[useRoundTimer] Cancelled notifications on pause');

      // Pause timer
      setTimerState(prevState => ({
        ...prevState,
        isRunning: false,
        targetTime: undefined,
      }));
    } else {
      playSound('beep');

      // Reschedule notifications from current segment with remaining time
      const hasPermission = await requestTimerNotificationPermissions();
      if (hasPermission) {
        await scheduleTimerNotifications(timerSequence, currentTimerIndex, segmentTimeLeft);
        logger.info('[useRoundTimer] Rescheduled notifications on resume');
      }

      // Resume timer - recalculate targetTime based on remaining time
      setTimerState(prevState => {
        const now = Date.now();
        const newTargetTime = now + (prevState.segmentTimeLeft * 1000);

        return {
          ...prevState,
          isRunning: true,
          targetTime: newTargetTime,
        };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: the listed deps are the intended triggers
  }, [timerState.timerStarted, timerState.isRunning, timerState.timerSequence, timerState.currentTimerIndex, timerState.segmentTimeLeft, startTimer, playSound]);

  // Advance to next segment
  const advanceTimer = useCallback(() => {
    setTimerState(prevState => {
      const { currentTimerIndex, timerSequence } = prevState;
      const nextTimerIndex = currentTimerIndex + 1;
      const totalTimers = timerSequence.length;
      
      // Calculate trained rounds
      const trainedRounds = 
        timerSequence[nextTimerIndex] && 
        timerSequence[nextTimerIndex].type === 'round'
          ? prevState.currentTrainRoundIndex + 1
          : prevState.currentTrainRoundIndex;
      
      // Check if timer is completed
      if (nextTimerIndex >= totalTimers) {
        playSound('complete');
        
        if (timerInterval.current) {
          window.clearInterval(timerInterval.current);
          timerInterval.current = null;
        }
        
        return {
          ...prevState,
          isCompleted: true,
          isRunning: false,
        };
      }
      
      // Speak the name of the next segment
      if (timerSequence[nextTimerIndex]) {
        speak(timerSequence[nextTimerIndex].label);
        playSound('bell');
      }

      return {
        ...prevState,
        currentTimerIndex: nextTimerIndex,
        currentTrainRoundIndex: trainedRounds,
      };
    });
  }, [speak, playSound]);

  // Reset timer
  const resetTimer = useCallback(async () => {
    const timerSequence = getTimerSequenceBasedOnConfig(timerConfig);

    // Cancel any scheduled notifications
    await cancelTimerNotifications();
    logger.info('[useRoundTimer] Cancelled notifications on reset');

    setTimerState(prevState => ({
      ...prevState,
      elapsedTime: 0,
      isRunning: false,
      timerStarted: false,
      isCompleted: false,
      currentTimerIndex: 0,
      currentTrainRoundIndex: 0,
      totalTime: getTotalTimerTime(timerConfig),
      segmentTimeLeft: timerSequence.length > 0 ? timerSequence[0].time : 0,
    }));

    if (timerInterval.current) {
      window.clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
  }, [timerConfig]);

  // Stop timer
  const stopTimer = useCallback(async () => {
    // Cancel any scheduled notifications
    await cancelTimerNotifications();
    logger.info('[useRoundTimer] Cancelled notifications on stop');

    setTimerState(prevState => ({
      ...prevState,
      isRunning: false,
      isCompleted: true,
    }));

    if (timerInterval.current) {
      window.clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
  }, []);

  // Toggle edit mode
  const toggleEditMode = useCallback(() => {
    if (timerState.timerStarted) {
      // Show error message (would be implemented with a UI library)
      logger.info('Cannot edit timer while it is running');
      return;
    }
    
    setIsEditMode(prevMode => !prevMode);
  }, [timerState.timerStarted]);

  // Update timer configuration
  const updateTimerConfig = useCallback((newConfig: TimerConfig) => {
    setTimerConfig(newConfig);
    setIsEditMode(false);
  }, []);

  // Get current segment
  const getCurrentSegment = useCallback((): TimerSegment | null => {
    const { currentTimerIndex, timerSequence } = timerState;
    
    if (timerSequence.length === 0 || currentTimerIndex >= timerSequence.length) {
      return null;
    }
    
    return timerSequence[currentTimerIndex];
  }, [timerState]);

  return {
    timerConfig,
    timerState,
    isEditMode,
    startTimer,
    toggleTimer,
    advanceTimer,
    resetTimer,
    stopTimer,
    toggleEditMode,
    updateTimerConfig,
    getCurrentSegment,
  };
};

export default useRoundTimer;